import React from 'react';
import PropTypes from 'prop-types';
import { Bookmark, BookmarkPlus, BookmarkMinus, BookmarkCheck } from 'lucide-react';
import { createClient } from '@/lib/supabase/component';
import { useSaveStore } from '@/lib/stores/post';
import Button from '@/components/Button/Button';
import { twMerge } from 'tailwind-merge';
import classNames from 'classnames';

export default function BookmarkButton({
  storyId,
  variant = 'icon',
  className,
  strokeWidth = 1.75,
  ...props
}) {
  const supabase = createClient();
  const {
    toggleSaveStory,
    isStorySaved,
  } = useSaveStore();

  // Get values from the store
  const isSaved = isStorySaved(storyId);

  const handleToggleSave = async () => {
    await toggleSaveStory(supabase, storyId);
  };

  // For ShareBar variant, use the same styling as other buttons
  if (variant === 'sharebar') {
    const classButtonBase = classNames(
      'group block size-[35px] overflow-hidden rounded-full transition-all',
      '!bg-navy dark:!bg-silver',
      '!text-white hover:!text-navy dark:hover:!text-silver',
      'hover:!bg-transparent !border-2 !border-solid !border-navy dark:!border-silver'
    );

    return (
      <button
        onClick={handleToggleSave}
        aria-label={isSaved ? 'Unsave' : 'Save'}
        title={isSaved ? 'Unsave' : 'Save'}
        className={twMerge(classButtonBase, className)}
        {...props}
      >
        {isSaved ? (
          <>
            <BookmarkCheck
              strokeWidth={strokeWidth}
              className={`group-hover:!hidden mx-auto size-5 [&_path+path]:stroke-navy dark:[&_path+path]:stroke-silver`}
              fill="currentColor"
            />
            <BookmarkMinus
              strokeWidth={strokeWidth}
              className={`!hidden group-hover:!block mx-auto size-5 [&_path+line]:stroke-[#fff]`}
              fill="currentColor"
            />
          </>
        ) : (
          <>
            <Bookmark
              strokeWidth={strokeWidth}
              className={`group-hover:!hidden mx-auto size-5`}
            />
            <BookmarkPlus
              strokeWidth={strokeWidth}
              className={`!hidden group-hover:!block mx-auto size-5`}
            />
          </>
        )}
      </button>
    );
  }

  // For icon variant (PostActions)
  return (
    <Button
      variant={variant}
      className={twMerge(
        'group relative',
        className
      )}
      onClick={handleToggleSave}
      aria-label={isSaved ? 'Unsave' : 'Save'}
      title={isSaved ? 'Unsave' : 'Save'}
      {...props}
    >
      {isSaved ? (
        <>
          <BookmarkCheck
            strokeWidth={strokeWidth}
            className={`group-hover:!hidden text-navy dark:text-silver !size-8 !mr-0 [&_path+path]:stroke-[#fff]`}
            fill='currentColor'
          />
          <BookmarkMinus
            strokeWidth={strokeWidth}
            className={`!hidden group-hover:!block text-navy dark:text-silver !size-8 !mr-0 [&_path+line]:stroke-[#fff]`}
            fill='currentColor'
          />
        </>
      ) : (
        <>
          <Bookmark
            strokeWidth={strokeWidth}
            className={`group-hover:!hidden text-navy dark:text-silver !size-8 !mr-0`}
          />
          <BookmarkPlus
            strokeWidth={strokeWidth}
            className={`!hidden group-hover:!block text-navy dark:text-silver !size-8 !mr-0`}
          />
        </>
      )}
    </Button>
  );
}

BookmarkButton.propTypes = {
  storyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  variant: PropTypes.oneOf(['icon', 'sharebar']),
  className: PropTypes.string,
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
};